import type { Node as ProseMirrorNode, NodeType, ResolvedPos } from '@tiptap/pm/model';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

import { SIGNATURE_NODE_NAME } from './signatureNode';

/**
 * Putting one signature BESIDE another.
 *
 * A signature is a block, so two of them stack — the model has no way to say "these share a
 * line" except the one a Word execution block already uses: a two-column row. The `Add a
 * side-by-side pair` button builds that container and the author fills it. This is the same
 * arrangement reached by dragging instead, which is how anyone actually expects to do it.
 *
 * Deliberately the SAME structure the button produces, not a second one. A `signatureRow` node
 * of its own would read more cleanly, but it would be a second way to express one thing that
 * every guard, traversal and renderer path would have to learn — and the table path is already
 * the one `flowRender`, `flowSignatureSlots` and the header-row refusal are written against.
 */

export type PairSide = 'left' | 'right';

export interface PairTarget {
	/** Document position of the signature being dropped beside. */
	pos: number;
	side: PairSide;
}

/** Marks the stamp itself inside an anchor's node view, so a drop can measure which half of it. */
export const SIGNATURE_STAMP_ATTRIBUTE = 'data-signature-stamp';

const CELL_TYPES = new Set(['tableCell', 'tableHeader']);

/**
 * Whether the anchor sits inside a list item.
 *
 * `flowRender` passes a list item's indent to a paragraph and to a signature, but NOT to a
 * table: a pair formed inside a bullet would render flush against the left margin, out from
 * under the bullet it was authored beneath. Refused rather than allowed to do that quietly —
 * the drop falls back to a normal insert, which still works.
 */
const insideListItem = ($pos: ResolvedPos): boolean => {
	for (let depth = $pos.depth; depth > 0; depth -= 1) {
		if ($pos.node(depth).type.name === 'listItem') {
			return true;
		}
	}
	return false;
};

const holdsSignature = (node: ProseMirrorNode): boolean => {
	let found = false;
	node.descendants((child) => {
		if (child.type.name === SIGNATURE_NODE_NAME) {
			found = true;
		}
		return !found;
	});
	return found;
};

/**
 * The signature a drop lands on, and which half of it the pointer is over.
 *
 * Takes a POINT rather than a drag event, because the two gestures that need this are not both
 * drags: a slot from the palette arrives as HTML5 drag-and-drop, and an anchor already in the
 * document is moved with pointer events so that the whole stamp can be the handle.
 *
 * `posAtCoords().inside` rather than a DOM walk: an anchor is an atom, so ProseMirror already
 * answers "which node are these coordinates inside" — and it answers it for the whole band,
 * which is what makes dropping into the empty space beside a stamp target that stamp rather
 * than nothing.
 *
 * The side is measured against the STAMP, not the band. They are different once the author has
 * moved the stamp across: a band spans the page, so its midpoint says "right" for a drop that
 * is plainly to the left of the stamp sitting in it.
 */
export const signaturePairTarget = (
	view: EditorView,
	point: { clientX: number; clientY: number }
): PairTarget | null => {
	const coords = view.posAtCoords({ left: point.clientX, top: point.clientY });
	if (coords === null || coords.inside < 0) {
		return null;
	}
	const node = view.state.doc.nodeAt(coords.inside);
	if (node === null || node.type.name !== SIGNATURE_NODE_NAME) {
		return null;
	}
	const dom = view.nodeDOM(coords.inside);
	if (!(dom instanceof HTMLElement)) {
		return null;
	}
	const stamp = dom.querySelector(`[${SIGNATURE_STAMP_ATTRIBUTE}]`) ?? dom;
	const rect = stamp.getBoundingClientRect();
	return {
		pos: coords.inside,
		side: point.clientX >= rect.left + rect.width / 2 ? 'right' : 'left',
	};
};

/** A one-row, two-column container holding the pair, borderless like the button's. */
const pairContainer = (
	schema: EditorState['schema'],
	left: ProseMirrorNode,
	right: ProseMirrorNode
): ProseMirrorNode | null => {
	const table: NodeType | undefined = schema.nodes.table;
	const row: NodeType | undefined = schema.nodes.tableRow;
	const cell: NodeType | undefined = schema.nodes.tableCell;
	if (table === undefined || row === undefined || cell === undefined) {
		return null;
	}
	// No attributes at all: column widths are shared equally across the printable width by
	// `tableFromEditor`, and an absent border width is what makes it render as two stamps
	// rather than as a ruled table. Exactly what `insertTable({cols: 2})` leaves behind.
	return table.create(
		null,
		row.create(null, [cell.create(null, left), cell.create(null, right)])
	);
};

/**
 * A transaction putting `signature` beside the target, or null when it cannot go there.
 *
 * `source` is the range the node came FROM, for a move within the document; a slot dragged in
 * from the palette has none. Deleted first and the target mapped through, so the two operations
 * cannot be ordered wrongly when the source sits before the target.
 */
export const pairSignature = (
	state: EditorState,
	target: PairTarget,
	signature: ProseMirrorNode,
	source?: { from: number; to: number }
): Transaction | null => {
	// Dropping a stamp beside itself is a no-op the caller must not consume, or the drag looks
	// like it worked and the anchor has not moved.
	if (source !== undefined && target.pos >= source.from && target.pos < source.to) {
		return null;
	}
	const transaction = state.tr;
	if (source !== undefined) {
		transaction.delete(source.from, source.to);
	}
	const pos = transaction.mapping.map(target.pos);
	const targetNode = transaction.doc.nodeAt(pos);
	if (targetNode === null || targetNode.type.name !== SIGNATURE_NODE_NAME) {
		return null;
	}
	const $pos = transaction.doc.resolve(pos);

	// Already in a two-column block: the pair goes in the cell next door, if it is free.
	if (CELL_TYPES.has($pos.parent.type.name)) {
		const cellDepth = $pos.depth;
		const rowDepth = cellDepth - 1;
		const index = $pos.index(rowDepth) + (target.side === 'right' ? 1 : -1);
		const neighbour = $pos.node(rowDepth).maybeChild(index);
		// A row has no cell on that side, or the cell already holds an anchor. Refused rather
		// than stacked: storage is unique on the slot, so a second anchor in one cell is a
		// stamp that silently never happens.
		if (neighbour === null || neighbour === undefined || holdsSignature(neighbour)) {
			return null;
		}
		const start = $pos.posAtIndex(index, rowDepth);
		transaction.replaceWith(start + 1, start + neighbour.nodeSize - 1, signature);
		return transaction.docChanged ? transaction : null;
	}

	// Block level: the target becomes half of a new two-column block.
	const container =
		target.side === 'right'
			? pairContainer(state.schema, targetNode, signature)
			: pairContainer(state.schema, signature, targetNode);
	if (container === null) {
		return null;
	}
	const index = $pos.index();
	if (insideListItem($pos) || !$pos.parent.canReplaceWith(index, index + 1, container.type)) {
		return null;
	}
	transaction.replaceWith(pos, pos + targetNode.nodeSize, container);
	return transaction.docChanged ? transaction : null;
};
